import useTvApi, { isAbort, messageOf } from "hooks/useTvApi";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Icon,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
} from "stories/Admin";
import {
  AI_LOG_ATTACHMENT_LABELS,
  layoutName,
  THEMES,
  themeName,
  TvAiLogItem,
  TvAiLogResponse,
  TvPosterDesign,
} from "types/TvPosterTypes";
import PageModal from "../LocationsPage/PageModal";
import {
  aiLogUrl,
  formatCount,
  formatLogTime,
  metaLine,
  previewUrl,
} from "./aiLog";
import styles from "./TvAiLogPage.module.scss";

/*
 * Superadmin: alle forespørsler moskeene har gjort i «Lag plakat» med AI –
 * hva de skrev, hvilke forslag de fikk, og om ett av dem ble lagret.
 */

const PAGE_SIZE = 30;

interface Mosque {
  orgNr: string;
  orgName: string;
}

interface Enlarged {
  image: string;
  design: TvPosterDesign;
  index: number;
}

/** Fargeprikk + «Oppsett · Tema» for et forslag. */
const DesignName: React.FC<{ design: TvPosterDesign }> = ({ design }) => {
  const theme = THEMES.find((t) => t.value === design.theme);
  return (
    <span className={styles.designName}>
      {theme && (
        <span
          className={styles.swatch}
          style={{ background: theme.background, borderColor: theme.accent }}
          aria-hidden="true"
        />
      )}
      {layoutName(design.layout)} · {themeName(design.theme)}
    </span>
  );
};

type Thumb = string | "failed" | null;

/** Forhåndsvisningene (JPEG) for én rad, hentet når raden blir synlig. */
const useLazyPreviews = (item: TvAiLogItem) => {
  const api = useTvApi();
  const ref = useRef<HTMLElement | null>(null);
  const count = item.suggestions?.length ?? 0;
  const [visible, setVisible] = useState(false);
  const [thumbs, setThumbs] = useState<Thumb[]>(() => Array(count).fill(null));

  useEffect(() => {
    if (visible || count === 0) return;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible, count]);

  useEffect(() => {
    if (!visible || count === 0) return;
    const controller = new AbortController();
    const urls: string[] = [];
    Array.from({ length: count }, (_, index) =>
      api<Blob>(previewUrl(item.id, index), "GET", undefined, {
        blob: true,
        signal: controller.signal,
      })
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          urls.push(url);
          setThumbs((prev) => prev.map((t, i) => (i === index ? url : t)));
        })
        .catch((e) => {
          if (isAbort(e)) return;
          setThumbs((prev) => prev.map((t, i) => (i === index ? "failed" : t)));
        }),
    );
    return () => {
      controller.abort();
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [visible, count, item.id, api]);

  return { ref, thumbs };
};

const AiLogRow: React.FC<{
  item: TvAiLogItem;
  onEnlarge: (enlarged: Enlarged) => void;
}> = ({ item, onEnlarge }) => {
  const { ref, thumbs } = useLazyPreviews(item);
  const suggestions = item.suggestions ?? [];
  const meta = metaLine(item);

  return (
    <article
      ref={ref as React.Ref<HTMLElement>}
      className={styles.row}
      aria-label={`Forespørsel ${formatLogTime(item.createdAt)}`}
    >
      <header className={styles.rowHead}>
        <span className={styles.stack}>
          <b>{item.mosqueName || item.orgNr}</b>
          <span className={styles.muted}>
            {formatLogTime(item.createdAt)} · {item.email || "ukjent bruker"}
          </span>
        </span>
        {item.savedPosterId != null && (
          <Badge tone="mint">
            <Icon name="check" size={13} strokeWidth={2.2} />
            Lagret
          </Badge>
        )}
      </header>

      <blockquote className={styles.prompt}>«{item.prompt}»</blockquote>

      {!!item.attachments?.length && (
        <div className={styles.chips}>
          {item.attachments.map((a) => (
            <Badge key={a} tone="grey">
              {AI_LOG_ATTACHMENT_LABELS[a] ?? a}
            </Badge>
          ))}
        </div>
      )}

      {suggestions.length > 0 && (
        <div className={styles.thumbs}>
          {suggestions.map((design, index) => {
            const thumb = thumbs[index];
            return (
              <figure key={index} className={styles.thumb}>
                <span className={styles.thumbBox}>
                  {thumb && thumb !== "failed" ? (
                    <button
                      type="button"
                      className={styles.thumbButton}
                      onClick={() => onEnlarge({ image: thumb, design, index })}
                      aria-label={`Vis forslag ${index + 1} større`}
                    >
                      <img src={thumb} alt={design.title} />
                    </button>
                  ) : thumb === "failed" ? (
                    <span className={styles.thumbFailed}>
                      Ingen forhåndsvisning
                    </span>
                  ) : (
                    <Skeleton height="100%" radius={10} />
                  )}
                </span>
                <figcaption>
                  <span className={styles.thumbTitle}>{design.title}</span>
                  <DesignName design={design} />
                </figcaption>
              </figure>
            );
          })}
        </div>
      )}

      {item.savedPosterId != null && (
        <div className={styles.saved}>
          {item.savedPosterImage && (
            <img
              src={item.savedPosterImage}
              alt=""
              className={styles.savedImg}
            />
          )}
          <span className={styles.stack}>
            <b>{item.savedPosterTitle || "Plakat"}</b>
            <span className={styles.muted}>
              Lagret som plakat
              {item.savedAt ? ` ${formatLogTime(item.savedAt)}` : ""}
            </span>
          </span>
        </div>
      )}

      {item.error && <p className={styles.error}>{item.error}</p>}

      {meta && <span className={styles.meta}>{meta}</span>}
    </article>
  );
};

const TvAiLogPage: React.FC = () => {
  const api = useTvApi();
  const [page, setPage] = useState(1);
  const [orgNr, setOrgNr] = useState("");
  const [mosques, setMosques] = useState<Mosque[]>([]);
  const [data, setData] = useState<TvAiLogResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enlarged, setEnlarged] = useState<Enlarged | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    api<Mosque[]>("/api/v1/mosques", "GET")
      .then((list) =>
        setMosques(
          (Array.isArray(list) ? list : [])
            .slice()
            .sort((a, b) => (a.orgName || "").localeCompare(b.orgName || "")),
        ),
      )
      .catch(() => setMosques([]));
  }, [api]);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    api<TvAiLogResponse>(aiLogUrl(page, PAGE_SIZE, orgNr), "GET", undefined, {
      signal: controller.signal,
    })
      .then((res) => {
        setData(res);
        setIsLoading(false);
      })
      .catch((e) => {
        if (isAbort(e)) return;
        setError(messageOf(e));
        setIsLoading(false);
      });
    return () => controller.abort();
  }, [api, page, orgNr, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const stats = data?.stats;
  const value = (n: number | undefined) =>
    isLoading && !data ? "–" : formatCount(n ?? 0);
  const tiles: Array<[string, React.ReactNode]> = [
    ["Forespørsler", value(stats?.generations)],
    ["Siste 7 dager", value(stats?.last7Days)],
    ["Moskeer", value(stats?.mosques)],
    ["Brukere", value(stats?.users)],
    ["Lagret som plakat", value(stats?.saved)],
    ["Plakater laget i studio", value(stats?.studioPosters)],
    ["Feil", value(stats?.errors)],
    ["Tokens", value(stats?.tokens)],
  ];

  const items = data?.items ?? [];
  const size = data?.size || PAGE_SIZE;
  const pageCount = data ? Math.ceil((data.total || 0) / size) : 0;

  return (
    <>
      <PageHeader
        title="AI-logg"
        subtitle="Hva moskeene har bedt AI om i «Lag plakat», forslagene de fikk, og hvilke som ble lagret."
        actions={
          <Button
            variant="secondary"
            icon={<Icon name="swap" size={16} />}
            onClick={reload}
            loading={isLoading}
          >
            Oppdater
          </Button>
        }
      />
      <SectionTabs />

      <div className={styles.stats}>
        {tiles.map(([label, v]) => (
          <div key={label} className={styles.stat}>
            <b>{v}</b>
            <span>{label}</span>
          </div>
        ))}
      </div>

      <div className={styles.filters}>
        <Select
          label="Moské"
          fieldClassName={styles.mosqueField}
          value={orgNr}
          onChange={(e) => {
            setOrgNr(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Alle moskeer</option>
          {mosques.map((m) => (
            <option key={m.orgNr} value={m.orgNr}>
              {m.orgName} ({m.orgNr})
            </option>
          ))}
        </Select>
      </div>

      <Card>
        {error ? (
          <Alert tone="error">{error}</Alert>
        ) : isLoading && !data ? (
          <div className={styles.list}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={220} radius={16} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon="spark"
            title="Ingen har laget plakater med AI ennå"
          />
        ) : (
          <div className={styles.list}>
            {items.map((item) => (
              <AiLogRow key={item.id} item={item} onEnlarge={setEnlarged} />
            ))}
          </div>
        )}
        <Pagination
          className={styles.pagination}
          page={page}
          pageCount={pageCount}
          onChange={(p) => {
            setPage(p);
            window.scrollTo?.({ top: 0 });
          }}
        />
      </Card>

      <PageModal
        open={!!enlarged}
        title={
          enlarged
            ? `Forslag ${enlarged.index + 1}: ${enlarged.design.title}`
            : ""
        }
        icon="spark"
        width={1000}
        onClose={() => setEnlarged(null)}
        footer={
          <Button variant="secondary" onClick={() => setEnlarged(null)}>
            Lukk
          </Button>
        }
      >
        {enlarged && (
          <div className={styles.enlarged}>
            <img src={enlarged.image} alt={enlarged.design.title} />
            <DesignName design={enlarged.design} />
          </div>
        )}
      </PageModal>
    </>
  );
};

export default TvAiLogPage;
